import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "ui";

import {
  LabelPicker,
  LabelPickerTrigger,
  type LabelPickerLabel,
} from "src/components/LabelPicker";

export interface LabelsCellProps {
  labels: LabelPickerLabel[];
  allLabels: LabelPickerLabel[];
  onSave: (labelIds: string[]) => Promise<unknown>;
}

function sameIds(a: string[], b: string[]) {
  if (a.length !== b.length) return false;

  const bSet = new Set(b);
  return a.every((id) => {
    return bSet.has(id);
  });
}

export function LabelsCell(props: LabelsCellProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);

  const attachedIds = props.labels.map((label) => {
    return label.id;
  });

  if (optimisticIds && sameIds(optimisticIds, attachedIds)) {
    setOptimisticIds(null);
  }

  const selectedIds = optimisticIds ?? attachedIds;

  const saveMutation = useMutation({
    mutationFn: (labelIds: string[]) => {
      return props.onSave(labelIds);
    },
    onError: () => {
      setOptimisticIds(null);
      toast.toast({
        title: t("labelPicker.saveFailed"),
        variant: "destructive",
      });
    },
  });

  function toggle(labelId: string) {
    const next = selectedIds.includes(labelId)
      ? selectedIds.filter((id) => {
          return id !== labelId;
        })
      : [...selectedIds, labelId];
    setOptimisticIds(next);
    saveMutation.mutate(next);
  }

  const shownLabels = optimisticIds
    ? props.allLabels.filter((label) => {
        return optimisticIds.includes(label.id);
      })
    : props.labels;

  return (
    <LabelPicker
      labels={props.allLabels}
      selectedIds={selectedIds}
      onToggle={toggle}
      allowCreate
      align="start"
      trigger={<LabelPickerTrigger labels={shownLabels} />}
    />
  );
}

export { createLabelsCell } from "src/components/createLabelsCell";
